import { lastValueFrom } from "rxjs";
import { createEffect, createResource, createSignal, For } from "solid-js";

import { LyricsEditor } from "./LyricsEditor";
import type { UtdoLyrics } from "../types";
import { action } from "../util/action";
import { useApi } from "../util/api";
import { panic } from "../util/panic";
import { success } from "../util/success";

export function Lyrics() {
    const api = useApi();
    const [ currentId, setCurrentId ] = createSignal<number|undefined>(undefined);
    const [ lyrics, { refetch }] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectLyrics());
            } catch (err: any) {
                panic(err);
            }
            return [] as UtdoLyrics[];
        },
        { initialValue: [] as UtdoLyrics[] },
    )

    const currentLyrics = (): UtdoLyrics => {
        const id = currentId();
        return lyrics().find((v) => v.id === id) ?? {
            id: 0,
            title: "",
            url: "",
            lyrics: "",
        };
    };

    let dialogRef!: HTMLDialogElement;

    createEffect(() => {
        const id = currentId();
        if (id === undefined) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    });

    const deleteLyrics = action(
        async (c: UtdoLyrics) => {
            if (!confirm(`Vous allez supprimer la chanson "${c.title}".\nContinuer ?`)) {
                return;
            }
            await lastValueFrom(api.deleteLyrics(c.id!));
        },
        refetch,
    );

    const editLyrics = (id?: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setCurrentId(id);
    };

    const save = async (lyrics: UtdoLyrics) => {
        try {
            if ((lyrics.id ?? 0) > 0) {
                if (await lastValueFrom(api.updateLyrics(lyrics))) {
                    success("Chanson enregistrée avec succès.");
                }
            } else {
                if (await lastValueFrom(api.insertLyrics(lyrics))) {
                    success("Chanson enregistrée avec succès.");
                }
            }
            setCurrentId(undefined);
            refetch();
        } catch (err: any) {
            panic(err);
        }
    };

    return <section id="admin-lyrics">
        <h2>Paroles</h2>
        <div>
            <div class="actions">
                <button onClick={editLyrics(0)}>Nouvelles paroles</button>
            </div>
            <ul>
                <For each={lyrics()}>
                    {(c) => <li>
                        <a href="#"
                            onClick={editLyrics(c.id!)}>{c.title}</a>
                        <div class="actions">
                            <a href="#" class="icon"
                                onClick={deleteLyrics(c)}>delete</a>
                        </div>
                    </li>}
                </For>
            </ul>
        </div>
        <dialog ref={dialogRef}>
            <LyricsEditor
                lyrics={currentLyrics()}
                validate={save}
                cancel={() => setCurrentId(undefined)}
                />
        </dialog>
    </section>;
}